import { reactive } from 'vue';
import { PaymentProcessor } from '@cognitoforms/types/server-types/payment/model/payment-processor';
import type { PaymentAccountMeta } from '@cognitoforms/types/server-types/core/services/payment-account-meta';
import { CognitoPaymentStatus } from '@cognitoforms/types/server-types/model/cognito-payment-status';
import type { CognitoPayment } from '@cognitoforms/types/server-types/model/cognito-payment';
import type { PaymentFilterCriteria } from '@cognitoforms/types/server-types/core/cognito-pay/payment-filter-criteria';
import type { BadgeType } from 'src/components/info/Badge.vue';
import { CognitoPayoutStatus } from '@cognitoforms/types/server-types/model/cognito-payout-status';
import { CognitoDisputeStatus } from '@cognitoforms/types/server-types/model/cognito-dispute-status';
import { CognitoDisputeReason } from '@cognitoforms/types/server-types/model/cognito-dispute-reason';
import type { CognitoDispute } from '@cognitoforms/types/server-types/model/cognito-dispute';
import { CognitoPayTableCurrencyDisplayMode } from '@cognitoforms/types/server-types/core/cognito-pay/cognito-pay-table-currency-display-mode';
import { addSpaces, toTitleCase } from 'src/utilities/stringUtils';
import { createDateRangeFilter, createDropdownFilter, createMultiSelectDropdownFilter, createAmountFilter, createCurrencyFilter, createDropdownOption, normalizeCurrency, resolveSelectedOptionsValue, findDropdownOption } from 'src/utilities/table-filter-utils';
import type { DropdownOption, FilterConfig } from 'src/utilities/table-filter-utils';
import type { FilterApplyValue, FilterApplyDimensions } from 'src/utilities/table-filter-telemetry-utils';
import { isDarkColorScheme, getHasCustomTheme } from 'src/views/admin/utils/org-appearance-utils';
import type { PayoutFilterCriteria } from '@cognitoforms/types/server-types/core/cognito-pay/payout-filter-criteria';
import type { DisputeFilterCriteria } from '@cognitoforms/types/server-types/core/cognito-pay/dispute-filter-criteria';
import { getCustomerAvatarInfo } from '@cognitoforms/api/services/submitter-person-service';
import { getThumbnailUrl } from '@cognitoforms/api/services/file-service';
import type { TablePageResult } from '@cognitoforms/api/services/query-table-page';

export const getOnboardedCognitoPayAccounts = (accounts: readonly PaymentAccountMeta[] | undefined | null): PaymentAccountMeta[] => {
	return accounts?.filter(
		account => account.Processor === PaymentProcessor.CognitoPay && account.IsOnboarded
	) ?? [];
};

export const isRemovedCognitoPayAccount = (account: PaymentAccountMeta): boolean =>
	account.Processor === PaymentProcessor.CognitoPay && !account.IsActive && !account.IsClosed;

export const isClosedCognitoPayAccount = (account: PaymentAccountMeta): boolean =>
	account.Processor === PaymentProcessor.CognitoPay && account.IsClosed;

export const getCognitoPayAccountSuffix = (account: PaymentAccountMeta): string => {
	if (isClosedCognitoPayAccount(account))
		return '(Closed)';
	return isRemovedCognitoPayAccount(account) ? '(Removed)' : '';
};

export const getCognitoPayAccountLabel = (account: PaymentAccountMeta): string =>
	[account.Name, getCognitoPayAccountSuffix(account)].filter(Boolean).join(' ');

// Removed (inactive) accounts stay visible in the dashboard so their historical data remains accessible
export const getDashboardCognitoPayAccounts = (accounts: readonly PaymentAccountMeta[] | undefined | null): PaymentAccountMeta[] => {
	return accounts?.filter(
		account => account.Processor === PaymentProcessor.CognitoPay && (account.IsOnboarded || !account.IsActive)
	) ?? [];
};

export const getIncompleteCognitoPayAccounts = (accounts: readonly PaymentAccountMeta[] | undefined | null): PaymentAccountMeta[] => {
	return accounts?.filter(
		account => account.Processor === PaymentProcessor.CognitoPay && !account.IsOnboarded && account.IsActive
	) ?? [];
};

export const getCognitoPayAccountToResumeOnboarding = (
	accounts: readonly PaymentAccountMeta[] | undefined | null,
	requestedAccountId?: string | null
): PaymentAccountMeta | undefined => {
	if (getDashboardCognitoPayAccounts(accounts).length > 0)
		return undefined;

	const incompleteAccounts = getIncompleteCognitoPayAccounts(accounts);
	return incompleteAccounts.find(account => account.Id === requestedAccountId) ?? incompleteAccounts[0];
};

/**
 * Apply-value resolver for the customer filter. Logs only whether the anonymous-customer
 * sentinel was chosen (`anonymous`) or a specific customer (`identified`); the customer
 * identity is never logged, so no PII leaves the client.
 */
export const getCustomerFilterApplyValue = <TFilter extends { CustomerEmail?: unknown }>(anonymousCustomerFilterValue: string): FilterApplyValue<TFilter> =>
	filterModel => filterModel.CustomerEmail === anonymousCustomerFilterValue ? 'anonymous' : 'identified';

/**
 * Apply-dimensions resolver for the form filter. Adds an `option-type` dimension recording whether
 * the applied selection was a whole `folder` of forms or a single `form`.
 */
export const getFormFilterApplyDimensions = <TFilter extends { FormIds?: string[]; FormSelection?: string }>(formAndFolderOptions: DropdownOption[]): FilterApplyDimensions<TFilter> =>
	filterModel => {
		const option = findDropdownOption(formAndFolderOptions, filterModel.FormSelection)
			?? findDropdownOption(formAndFolderOptions, resolveSelectedOptionsValue(formAndFolderOptions, filterModel.FormIds));
		if (!option)
			return null;
		return { 'data-cf-option-type': option.icon === 'folder' ? 'folder' : 'form' };
	};

/**
 * Apply-dimensions resolver for the currency filter on tables that offer a form/balance display
 * mode. Adds a `currency-type` dimension recording whether the selected currency filters by the
 * form's original currency or the converted balance currency.
 */
export const getCurrencyModeApplyDimensions = <TFilter extends { CurrencyDisplayMode?: CognitoPayTableCurrencyDisplayMode }>(): FilterApplyDimensions<TFilter> =>
	filterModel => {
		if (filterModel.CurrencyDisplayMode === undefined || filterModel.CurrencyDisplayMode === null)
			return null;
		return { 'data-cf-currency-type': filterModel.CurrencyDisplayMode === CognitoPayTableCurrencyDisplayMode.ConvertedSpecific ? 'balance' : 'form' };
	};

type CachedCustomerInfo = {
	avatarUrl: string;
	name: string;
};

const customerAvatarCache = reactive<Record<string, CachedCustomerInfo>>({});

export const fetchAndCacheAvatarUrls = async (entryIds: string[]): Promise<void> => {
	const uncachedIds = entryIds.filter(id => !Object.prototype.hasOwnProperty.call(customerAvatarCache, id));
	if (!uncachedIds.length)
		return;
	await refreshAvatarUrls(uncachedIds);
};

export const refreshAvatarUrls = async (entryIds: string[]): Promise<void> => {
	if (!entryIds.length)
		return;
	const infoById = await getCustomerAvatarInfo(entryIds);
	// Cache an empty entry for ids the server omitted so we don't keep re-requesting entries without avatar or name.
	for (const id of entryIds) {
		const info = infoById[id];
		customerAvatarCache[id] = {
			avatarUrl: info?.AvatarId ? getThumbnailUrl(info.AvatarId) : '',
			name: info?.Name ?? ''
		};
	}
};

export const getCachedAvatarUrl = (entryId: string | undefined): string | undefined =>
	entryId ? customerAvatarCache[entryId]?.avatarUrl : undefined;

export const getCachedCustomerName = (entryId: string | undefined): string | undefined =>
	entryId ? customerAvatarCache[entryId]?.name : undefined;

type CognitoPayFxRateTextOptions = {
	sourceAmount?: number | null;
	targetAmount?: number | null;
	exchangeRate?: number | null;
};

export function getCognitoPayFxRateText(
	sourceCurrency: string | null | undefined,
	targetCurrency: string | null | undefined,
	locale: string = 'en-US',
	options: CognitoPayFxRateTextOptions = {}
): string | null {
	const normalizedSourceCurrency = normalizeCurrency(sourceCurrency);
	const normalizedTargetCurrency = normalizeCurrency(targetCurrency);
	if (!normalizedSourceCurrency || !normalizedTargetCurrency || normalizedSourceCurrency === normalizedTargetCurrency)
		return null;

	let rate = options.exchangeRate;
	if (rate == null && options.sourceAmount != null && options.targetAmount != null && options.sourceAmount !== 0)
		rate = Math.abs(options.targetAmount) / Math.abs(options.sourceAmount);

	if (rate == null)
		return null;

	const formattedRate = new Intl.NumberFormat(locale, { maximumFractionDigits: 6 }).format(rate);
	return `1 ${normalizedSourceCurrency} = ${formattedRate} ${normalizedTargetCurrency}`;
}

/**
 * The total already refunded on a payment, counting Succeeded and Pending refunds.
 */
export function getRefundedAmount(payment: Pick<CognitoPayment, 'Refunds'>): number {
	return (payment.Refunds ?? []).reduce(
		(sum, refund) => (refund.Status === 'Succeeded' || refund.Status === 'Pending') ? sum + refund.Amount : sum,
		0
	);
}

/**
 * The amount still available to refund on a payment: the original amount minus refunds already issued and any reversed funds.
 */
export function getMaxRefundableAmount(payment: Pick<CognitoPayment, 'Amount' | 'Refunds' | 'AmountReversed'>): number {
	return Math.round((payment.Amount - getRefundedAmount(payment) - (payment.AmountReversed ?? 0)) * 100) / 100;
}

/**
 * Prepares a fetched page of customer-keyed records (orders, payments, disputes) for display.
 * Warms the customer-avatar cache and maps each item through the caller-supplied display transform.
 */
export async function processCustomerPage<TItem extends { CustomerEntryId?: string | null }, TDisplay>(
	page: TablePageResult<TItem>,
	transform: (item: TItem) => TDisplay
): Promise<TablePageResult<TDisplay>> {
	const entryIds = [...new Set(page.Items.map(i => i.CustomerEntryId).filter((id): id is string => !!id))];
	await fetchAndCacheAvatarUrls(entryIds);
	return { ...page, Items: page.Items.map(transform) };
}

export function getCognitoPayPaymentStatusGroup(status: string): BadgeType | undefined {
	const normalize = (s?: string) =>
		(s ?? '')
			.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
			.toLowerCase()
			.trim();
	if (!status)
		return undefined;
	else
		status = normalize(status);
	switch (status) {
		case 'succeeded':
			return 'success';
		case 'partially refunded':
		case 'partially reversed':
			return 'warning';
		case 'pending':
			return 'alt';
		case 'disputed':
		case 'refunded':
		case 'canceled':
		case 'failed':
		case 'declined':
		case 'reversed':
		case 'blocked': // Note: not one of our current statuses, but adding this here to it appears in the correct summary segment
			return 'error';
		default:
			return undefined;
	}
}

const getCognitoPaymentStatusOptions = (): DropdownOption[] => {
	const partiallyPaid: DropdownOption[] = [
		CognitoPaymentStatus.PartiallyRefunded, CognitoPaymentStatus.PartiallyReversed
	].map(status =>
		createDropdownOption({ label: addSpaces(status), value: status, badgeColor: 'warning' })
	);

	const unsuccessful: DropdownOption[] = [
		CognitoPaymentStatus.Disputed,
		CognitoPaymentStatus.Reversed,
		CognitoPaymentStatus.Refunded,
		CognitoPaymentStatus.Declined,
		CognitoPaymentStatus.Canceled
	].map(status =>
		createDropdownOption({ label: addSpaces(status), value: status, badgeColor: 'error' })
	);

	return [
		createDropdownOption({ label: CognitoPaymentStatus.Succeeded, value: CognitoPaymentStatus.Succeeded, dotColor: 'success', badgeColor: 'success' }),
		createDropdownOption({ label: 'Partially Paid', value: 'Partially Paid', dotColor: 'warning', children: partiallyPaid }),
		createDropdownOption({ label: 'Unsuccessful', value: 'Unsuccessful', dotColor: 'error', children: unsuccessful }),
		createDropdownOption({ label: CognitoPaymentStatus.Pending, value: CognitoPaymentStatus.Pending, dotColor: 'alt', badgeColor: 'alt' })
	];
};

type CommonEntryFilterParams = {
	formAndFolderOptions: DropdownOption[],
	customerOptions: DropdownOption[],
	anonymousCustomerFilterValue?: string,
	accountOptions?: DropdownOption[],
	availableOriginalCurrencies?: string[],
	availableConvertedCurrencies?: string[],
	selectedCurrency?: string,
	loadCustomerAvatars?: (entryIds: string[]) => Promise<void>,
	resolveCustomerAvatarUrl?: (entryId: string) => string | undefined
};

function createCurrencyFilters<TFilter extends { CurrencyDisplayMode?: CognitoPayTableCurrencyDisplayMode; SelectedCurrency?: string }>(params: CommonEntryFilterParams, groupKey: string) {
	return [
		createCurrencyFilter<TFilter>({
			groupKey,
			label: 'Currency',
			icon: 'currency',
			modeKey: 'CurrencyDisplayMode',
			currencyKey: 'SelectedCurrency',
			originalCurrencies: params.availableOriginalCurrencies ?? [],
			convertedCurrencies: params.availableConvertedCurrencies ?? [],
			originalActionLabel: 'Filter by Form Currency',
			convertedActionLabel: 'Filter by Balance Currency',
			originalCurrencyPlaceholder: 'Select form currency',
			convertedCurrencyPlaceholder: 'Select balance currency',
			originalSelectedLabelPrefix: 'Form Currency:',
			convertedSelectedLabelPrefix: 'Balance Currency:'
		})
	];
}

function getAvailableCurrencies(
	currencyDisplayMode: CognitoPayTableCurrencyDisplayMode | null | undefined,
	availableOriginalCurrencies: string[] = [],
	availableConvertedCurrencies: string[] = []
) {
	return currencyDisplayMode === CognitoPayTableCurrencyDisplayMode.ConvertedSpecific
		? availableConvertedCurrencies
		: currencyDisplayMode === CognitoPayTableCurrencyDisplayMode.OriginalSpecific
			? availableOriginalCurrencies
			: [];
}

export function sanitizeCognitoPayCurrencyFilters<TFilter extends { CurrencyDisplayMode?: CognitoPayTableCurrencyDisplayMode | null; SelectedCurrency?: string | null }>(
	filters: Partial<TFilter>,
	availableOriginalCurrencies: string[] = [],
	availableConvertedCurrencies: string[] = []
): Partial<TFilter> {
	const nextFilters = { ...filters };
	const currencyDisplayMode = nextFilters.CurrencyDisplayMode ?? CognitoPayTableCurrencyDisplayMode.OriginalMixed;

	if (currencyDisplayMode === CognitoPayTableCurrencyDisplayMode.OriginalMixed) {
		nextFilters.CurrencyDisplayMode = CognitoPayTableCurrencyDisplayMode.OriginalMixed;
		nextFilters.SelectedCurrency = undefined;
		return nextFilters;
	}

	const availableCurrencies = getAvailableCurrencies(currencyDisplayMode, availableOriginalCurrencies, availableConvertedCurrencies);
	const selectedCurrency = availableCurrencies.find(currency => normalizeCurrency(currency) === normalizeCurrency(nextFilters.SelectedCurrency));
	if (!selectedCurrency) {
		nextFilters.CurrencyDisplayMode = CognitoPayTableCurrencyDisplayMode.OriginalMixed;
		nextFilters.SelectedCurrency = undefined;
		return nextFilters;
	}

	nextFilters.CurrencyDisplayMode = currencyDisplayMode;
	nextFilters.SelectedCurrency = selectedCurrency;
	return nextFilters;
}

/**
 * Builds the shared filter configs used by both the payments and disputes tables:
 * customer, form, account (conditional), and amount.
 */
function createCommonCognitoPayFilters<TFilter extends Record<string, unknown>>(params: CommonEntryFilterParams) {
	const currencyAmountGroupKey = 'cognito-pay-currency-amount';
	const currencyFilters = createCurrencyFilters<TFilter & { CurrencyDisplayMode?: CognitoPayTableCurrencyDisplayMode; SelectedCurrency?: string }>(params, currencyAmountGroupKey);
	return {
		customer: createDropdownFilter<TFilter>({
			key: 'CustomerEmail',
			label: 'Customer',
			icon: 'person-form',
			options: params.customerOptions,
			searchable: true,
			searchPlaceholder: 'Search...',
			showValueAsSubtext: true,
			showAvatarInOptions: true,
			excludeValuesFromSearch: params.anonymousCustomerFilterValue ? [params.anonymousCustomerFilterValue] : undefined,
			loadAvatars: params.loadCustomerAvatars,
			resolveAvatarUrl: params.resolveCustomerAvatarUrl
		}),
		form: createMultiSelectDropdownFilter<TFilter>({
			selectedOptionsKey: 'FormIds',
			selectedValueKey: 'FormSelection',
			label: 'Form',
			icon: 'form',
			searchable: true,
			searchPlaceholder: 'Search...',
			allCollapsedByDefault: true,
			excludeValuesFromSearch: ['archivedItems', 'deletedItems'],
			options: params.formAndFolderOptions
		}),
		accountFilters: params.accountOptions ? [
			createDropdownFilter<TFilter>({
				key: 'ProviderAccountId',
				label: 'Account',
				icon: 'users',
				searchable: false,
				options: params.accountOptions
			})
		] : [],
		currencyFilters,
		amount: createAmountFilter({
			key: 'amount',
			columnKey: 'FormattedAmount',
			label: 'Amount',
			icon: '',
			groupKey: currencyAmountGroupKey,
			exactKey: 'ExactAmount',
			minKey: 'MinAmount',
			maxKey: 'MaxAmount',
			currency: params.selectedCurrency,
			min: 0,
			step: 0.01
		})
	};
}

/**
 * Generates configuration for Cognito Pay Payments Table Filter
 */
export const createCognitoPayPaymentFilters = (params: CommonEntryFilterParams & {
	earliestPaymentDate?: Date
}): FilterConfig<PaymentFilterCriteria>[] => {
	const { customer, form, accountFilters, currencyFilters, amount } = createCommonCognitoPayFilters<PaymentFilterCriteria>(params);
	return [
		createDateRangeFilter({
			key: 'date',
			label: 'Date',
			startKey: 'FromDate',
			endKey: 'ToDate',
			minDate: params.earliestPaymentDate,
			maxDate: new Date()
		}),
		createMultiSelectDropdownFilter<PaymentFilterCriteria>({
			selectedOptionsKey: 'Statuses',
			label: 'Status',
			icon: 'status-ring',
			useDotsAndBadges: true,
			options: getCognitoPaymentStatusOptions()
		}),
		customer,
		form,
		...accountFilters,
		...currencyFilters,
		amount
	];
};

// Disputes
export function getDisputeDisplayReason(dispute: Pick<CognitoDispute, 'Reason' | 'ProviderReason'>): string {
	return dispute.Reason === 'Other' && dispute.ProviderReason?.trim()
		? toTitleCase(dispute.ProviderReason.replace(/[_-]+/g, ' ').trim().toLowerCase())
		: dispute.Reason;
}

export function getCognitoPayDisputeStatusGroup(status: string): BadgeType | undefined {
	if (!status)
		return undefined;
	else
		status = status.toLowerCase().trim();

	switch (status) {
		case 'won':
			return 'success';
		case 'lost':
		case 'inquiry closed':
			return 'alt';
		case 'inquiry under review':
		case 'under review':
			return 'warning';
		case 'inquiry needs response':
		case 'needs response':
			return 'error';
		default:
			return undefined;
	}
}

const getCognitoPayDisputeStatusOptions = (): DropdownOption[] => {
	const underReview: DropdownOption[] = [
		CognitoDisputeStatus.UnderReview, CognitoDisputeStatus.InquiryUnderReview
	].map(status =>
		createDropdownOption({ label: addSpaces(String(status)), value: String(status), badgeColor: 'warning' })
	);

	const needsResponse: DropdownOption[] = [
		CognitoDisputeStatus.NeedsResponse, CognitoDisputeStatus.InquiryNeedsResponse
	].map(status =>
		createDropdownOption({ label: addSpaces(String(status)), value: String(status), badgeColor: 'error' })
	);

	const closed: DropdownOption[] = [
		CognitoDisputeStatus.Lost, CognitoDisputeStatus.InquiryClosed
	].map(status =>
		createDropdownOption({ label: addSpaces(String(status)), value: String(status), badgeColor: 'alt' })
	);

	return [
		createDropdownOption({ label: CognitoDisputeStatus.Won, value: CognitoDisputeStatus.Won, dotColor: 'success', badgeColor: 'success' }),
		createDropdownOption({ label: 'Under Review', value: 'Under Review', dotColor: 'warning', children: underReview }),
		createDropdownOption({ label: 'Needs Response', value: 'Needs Response', dotColor: 'error', children: needsResponse }),
		createDropdownOption({ label: 'Closed', value: 'Closed', dotColor: 'disabled', children: closed })
	];
};

/**
 * Generates configuration for Cognito Pay Disputes Table Filter
 */
export const createCognitoPayDisputeFilters = (params: CommonEntryFilterParams & {
	earliestDisputeDate?: Date,
	earliestRespondByDate?: Date,
	latestRespondByDate?: Date
}): FilterConfig<DisputeFilterCriteria>[] => {
	const { customer, form, accountFilters, currencyFilters } = createCommonCognitoPayFilters<DisputeFilterCriteria>(params);
	return [
		createDateRangeFilter({
			key: 'date',
			label: 'Disputed On',
			startKey: 'FromDate',
			endKey: 'ToDate',
			minDate: params.earliestDisputeDate,
			maxDate: new Date()
		}),
		createDateRangeFilter({
			key: 'respond-date',
			label: 'Respond By',
			startKey: 'RespondByFromDate',
			endKey: 'RespondByToDate',
			minDate: params.earliestRespondByDate,
			maxDate: params.latestRespondByDate
		}),
		createMultiSelectDropdownFilter<DisputeFilterCriteria>({
			selectedOptionsKey: 'Statuses',
			label: 'Status',
			icon: 'status-ring',
			useDotsAndBadges: true,
			options: getCognitoPayDisputeStatusOptions()
		}),
		createDropdownFilter<DisputeFilterCriteria>({
			key: 'Reason',
			label: 'Reason',
			icon: 'support',
			options: Object.values(CognitoDisputeReason).map(reason =>
				createDropdownOption({ label: addSpaces(String(reason)), value: String(reason) })
			)
		}),
		customer,
		form,
		...accountFilters,
		...currencyFilters,
		createAmountFilter({
			key: 'amount',
			label: 'Disputed Amount',
			icon: '',
			exactKey: 'ExactAmount',
			minKey: 'MinAmount',
			maxKey: 'MaxAmount',
			currency: params.selectedCurrency,
			min: 0,
			step: 0.01
		})
	];
};

const getCognitoPayoutStatusOptions = (): DropdownOption[] => {
	const incomplete: DropdownOption[] = [
		CognitoPayoutStatus.Pending, CognitoPayoutStatus.Sent
	].map(status =>
		createDropdownOption({ label: addSpaces(status), value: status, badgeColor: 'warning' })
	);

	const unsuccessful: DropdownOption[] = [
		CognitoPayoutStatus.Canceled, CognitoPayoutStatus.Failed
	].map(status =>
		createDropdownOption({ label: addSpaces(status), value: status, badgeColor: 'error' })
	);

	return [
		createDropdownOption({ label: CognitoPayoutStatus.Paid, value: CognitoPayoutStatus.Paid, dotColor: 'success', badgeColor: 'success' }),
		createDropdownOption({ label: 'Incomplete', value: 'Incomplete', dotColor: 'warning', children: incomplete }),
		createDropdownOption({ label: 'Unsuccessful', value: 'Unsuccessful', dotColor: 'error', children: unsuccessful })
	];
};

export function getCognitoPayPayoutStatusGroup(status: string): BadgeType {
	if (!status)
		return 'warning';
	else
		status = status.toLowerCase().trim();

	switch (status) {
		case 'paid':
			return 'success';
		case 'canceled':
		case 'failed':
			return 'error';
		case 'pending':
		case 'sent':
			return 'warning';
		default:
			return 'warning';
	}
}

/**
 * Generates configuration for Cognito Pay Payouts Table Filter
 */
export const createCognitoPayPayoutFilters = (params: {
	bankOptions?: DropdownOption[],
	earliestPayoutDate?: Date,
	currencyValues?: string[]
}): FilterConfig<PayoutFilterCriteria>[] => [
	createDateRangeFilter({
		key: 'date',
		label: 'Date',
		startKey: 'FromDate',
		endKey: 'ToDate',
		minDate: params.earliestPayoutDate,
		maxDate: new Date()
	}),
	createMultiSelectDropdownFilter<PayoutFilterCriteria>({
		selectedOptionsKey: 'Statuses',
		label: 'Status',
		icon: 'status-ring',
		useDotsAndBadges: true,
		options: getCognitoPayoutStatusOptions()
	}),
	...(params.bankOptions ? [createDropdownFilter<PayoutFilterCriteria>({
		key: 'BankAccountId',
		label: 'Destination',
		icon: 'bank',
		searchable: true,
		searchPlaceholder: 'Search...',
		options: params.bankOptions
	})] : []),
	createCurrencyFilter<PayoutFilterCriteria>({
		currencyKey: 'Currency',
		currencies: params.currencyValues ?? [],
		label: 'Currency',
		icon: 'currency',
		groupKey: 'cognito-pay-payout-currency-amount'
	}),
	createAmountFilter({
		key: 'amount',
		label: 'Amount',
		icon: '',
		exactKey: 'ExactAmount',
		minKey: 'MinAmount',
		maxKey: 'MaxAmount',
		min: 0,
		step: 0.01,
		groupKey: 'cognito-pay-payout-currency-amount'
	})
];

// Helpers for getRGB function

const removeTrimmedQuotes = (s: string) => s.replace(/^['"]|['"]$/g, ''); // remove leading/trailing quotes if present

// Helper: round any floating numbers in color strings to 2 decimal places
const normalizeDecimals = (s: string) => s.replace(/([0-9]*\.[0-9]+)/g, (m) => {
	const n = parseFloat(m);
	// keep two decimal places
	return n.toFixed(2);
});

// Convert CSS Color 4 "color(srgb ... [/ alpha])" into rgba(...) string
const convertColorSrgbToRgba = (colorString: string): string | undefined => {
	// match color(srgb r g b / a) with optional alpha
	const matches = colorString.trim().match(/^color\(\s*srgb\s+([0-9.]+)\s+([0-9.]+)\s+([0-9.]+)(?:\s*\/\s*([0-9.]+))?\s*\)$/i);
	if (!matches)
		return undefined;
	const r = Math.round(parseFloat(matches[1]) * 255);
	const g = Math.round(parseFloat(matches[2]) * 255);
	const b = Math.round(parseFloat(matches[3]) * 255);
	const a = matches[4] ? parseFloat(matches[4]) : 1;
	const aStr = a.toFixed(2).replace(/\.?0+$/, '');
	return `rgba(${r}, ${g}, ${b}, ${aStr})`;
};

// Accessor for computed CSS custom properties
const getRGB = (name: string): string | undefined => {
	if (typeof document === 'undefined')
		return undefined;

	const computedStyle = getComputedStyle(document.documentElement);
	if (!computedStyle)
		return undefined;

	// Resolve chained var(...) references up to a safe depth
	let value = removeTrimmedQuotes(computedStyle.getPropertyValue(name)?.trim() || '');
	let depth = 0;
	const maxDepth = 8;
	const varRefRegex = /^var\(\s*(--[a-zA-Z0-9-_]+)\s*(?:,\s*([^)]+))?\s*\)$/; // matches var(--token) or var(--token, fallback)

	while (depth < maxDepth) {
		const varMatches = value.match(varRefRegex);
		if (!varMatches)
			break;
		const refName = varMatches[1];
		const fallback = (varMatches[2] || '').trim();
		const resolved = computedStyle.getPropertyValue(refName)?.trim();
		if (!resolved) {
			if (fallback) {
				value = removeTrimmedQuotes(fallback).trim();
				break;
			}
			// unresolved var -> bail
			value = '';
			break;
		}
		value = removeTrimmedQuotes(resolved).trim();
		depth++;
	}

	if (!value)
		return undefined;

	// If the value includes a color-mix (or other computed color expression),
	// ask the browser to compute it by applying it to a temporary element.
	if (/^color(-mix)?\(/i.test(value) || /^(rgba?|hsla?)\(/i.test(value) || value === 'transparent' || /^(#|calc\()/i.test(value)) {
		try {
			const el = document.createElement('div');
			el.style.position = 'absolute';
			el.style.left = '-9999px';
			el.style.top = '-9999px';
			el.style.width = '1px';
			el.style.height = '1px';
			el.style.pointerEvents = 'none';
			// Set as background so browser resolves color-mix, var(...) etc.
			el.style.background = value;
			const container = document.body || document.documentElement;
			container.appendChild(el);
			const resolved = getComputedStyle(el).backgroundColor || '';
			container.removeChild(el);
			const final = resolved.trim();
			if (final) {
				// if browser returned CSS Color 4 form, convert to rgba(...)
				const converted = convertColorSrgbToRgba(final);
				const rawOutput = converted ?? normalizeDecimals(final);

				// For --bg-overlay we allow rgba. For all other tokens,
				// ensure we return an rgb value.
				if (name === '--bg-overlay')
					return rawOutput;

				// Convert rgba(...) -> rgb(...) by dropping alpha (round r,g,b)
				const rgbaMatch = rawOutput.match(/^rgba?\(\s*([0-9.]+)[,\s]+([0-9.]+)[,\s]+([0-9.]+)(?:[,\s/]+([0-9.]+))?\s*\)$/i);
				if (rgbaMatch) {
					const r = Math.round(parseFloat(rgbaMatch[1]));
					const g = Math.round(parseFloat(rgbaMatch[2]));
					const b = Math.round(parseFloat(rgbaMatch[3]));
					return `rgb(${r}, ${g}, ${b})`;
				}

				// otherwise return whatever the browser gave (hex, named color, etc.)
				return rawOutput;
			}
		}
		catch {
			// fall through to return raw string
		}
	}

	// Otherwise return the resolved raw value (e.g. "rgba(...)" or "#rrggbb" or plain token)
	// also handle color(srgb ...) here if present
	const convertedRaw = convertColorSrgbToRgba(value);
	const output = convertedRaw ?? normalizeDecimals(value);

	// If this is not the overlay token, ensure we return rgb(...) instead of rgba(...)
	if (name !== '--bg-overlay') {
		const rgbaMatch = output.match(/^rgba?\(\s*([0-9.]+)[,\s]+([0-9.]+)[,\s]+([0-9.]+)(?:[,\s/]+([0-9.]+))?\s*\)$/i);
		if (rgbaMatch) {
			const r = Math.round(parseFloat(rgbaMatch[1]));
			const g = Math.round(parseFloat(rgbaMatch[2]));
			const b = Math.round(parseFloat(rgbaMatch[3]));
			return `rgb(${r}, ${g}, ${b})`;
		}
	}

	return output;
};

export type OrgCustomColors = Record<string, string | undefined>;
// Color-only helper to produce Stripe appearance.variables
export function getStripeColorVariables(orgColors?: OrgCustomColors): OrgCustomColors {
	const result: OrgCustomColors = {
		fontFamily: getRGB('--font-body'),
		fontSizeBase: getRGB('--text-2'),
		bodySmFontSize: getRGB('--text-2'),
		colorText: getRGB('--text-primary'),
		colorBackground: getRGB('--bg-primary'),
		colorPrimary: getRGB('--text-intense'),
		colorSecondaryText: getRGB('--text-primary'),
		offsetBackgroundColor: (isDarkColorScheme() && !getHasCustomTheme()) ? getRGB('--bg-code') : getRGB('--bg-selected'),
		borderRadius: '0px',
		// Headings
		headingSmFontSize: getRGB('--text-4'),
		headingSmTextTransform: 'capitalize', // component headings
		labelSmFontSize: getRGB('--text-2'),
		labelMdFontSize: getRGB('--text-2'),
		headingMdTextTransform: 'capitalize', // modal headings
		// Buttons
		buttonSecondaryColorBackground: getRGB('--bg-primary'), // because we can't say transparent, this token only accepts rbg, hsl, hex, no alpha values
		buttonSecondaryColorText: getRGB('--text-primary'),
		buttonSecondaryColorBorder: getRGB('--border-primary'),
		// Inputs
		colorBorder: getRGB('--border-primary'),
		formHighlightColorBorder: getRGB('--bg-primary'),
		formBorderRadius: '0px',
		formAccentColor: getRGB('--text-intense'),
		buttonBorderRadius: '0px',
		// Error
		colorDanger: getRGB('--icon-error-high-contrast'), // Since a lot of Stripe's errors are inline directly on the bg color, using the high contrast icon var that was created for that
		// Links
		actionPrimaryTextDecorationLine: 'underline',
		actionSecondaryTextDecorationLine: 'underline',
		actionSecondaryColorText: getRGB('--text-intense'),
		// Warning Badge
		badgeBorderRadius: '24px',
		badgeWarningColorText: getHasCustomTheme() ? getRGB('--text-warning') : getRGB('--text-primary'),
		badgeWarningColorBackground: getRGB('--bg-warning'),
		badgeWarningColorBorder: getRGB('--border-warning'),
		// Error Badge
		badgeDangerColorText: getHasCustomTheme() ? getRGB('--text-error') : getRGB('--text-primary'),
		badgeDangerColorBackground: getRGB('--bg-error'),
		badgeDangerColorBorder: getRGB('--red-3'),
		// Success Badge
		badgeSuccessColorText: getHasCustomTheme() ? getRGB('--text-success') : getRGB('--text-primary'),
		badgeSuccessColorBackground: getRGB('--bg-success'),
		badgeSuccessColorBorder: getRGB('--green-3'),
		// Info Badge
		badgeNeutralColorText: getHasCustomTheme() ? getRGB('--text-info') : getRGB('--text-primary'),
		badgeNeutralColorBackground: getRGB('--bg-info'),
		badgeNeutralColorBorder: getRGB('--border-info'),
		// Overlay
		overlayBackdropColor: getRGB('--bg-overlay')
	};

	return result;
}
